import {useState, useMemo} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {Play, XCircle, Info, Pencil, Trash2} from 'lucide-react'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {CertificationReviewControllerReviewItemsEndpoint} from '@/types/endpoint-contracts'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Progress} from '@/components/ui/progress'
import {Input} from '@/components/ui/input'
import {DatePicker} from '@/components/shared/DatePicker'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {SortableHeader} from '@/components/ui/sortable-header'
import {Pagination} from '@/components/ui/pagination'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {PulseDot} from '@/components/shared/PulseDot'
import {SegmentedProgress} from '@/components/ui/progress'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {formatDate, formatTimestamp} from '@/lib/date-utils'
import {
    CertificationCampaignStatus,
    CampaignOrigin,
    ReviewItemStatus,
    CertificationReviewItemResponseFields,
} from '@/types/certification'
import type {
    UpdateCampaignRequest,
} from '@/types/certification'
import {PageRequestFields} from '@/types/page'
import {PageLayout} from '@/components/shared/PageLayout'
import {MutedText} from '@/components/shared/MutedText'
import {TextLink} from '@/components/shared/TextLink'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {OwnersSection, OwnersEntityType} from '@/components/owners/OwnersSection'
import {useAppConfig} from '@/hooks/use-app-config'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {StatValue} from '@/components/shared/StatValue'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {InlineRow} from '@/components/shared/InlineRow'
import {SplitRow} from '@/components/shared/SplitRow'
import {Icon} from '@/components/shared/Icon'
import {campaignOriginLabel} from '@/features/certification/certification-badges'
import {CertificationCampaignStatusBadge, ReviewItemStatusBadge} from '@/types/certification'

export function CertificationCampaignDetail() {
    const services = useServices()
    const {id} = useParams<{id: string}>()
    const navigate = useNavigate()
    const i18n = useI18n()
    usePageTitle(i18n.certificationCampaigns.detailPageTitle)
    const {hasPermission} = useAuth()
    const canManage = hasPermission(permissions.certifications.create)
    const {certificationCampaignResponsibilityTypes} = useAppConfig()

    const activateDialog = useDialog()
    const cancelDialog = useDialog()
    const deleteDialog = useDialog()
    const editDialog = useDialog()
    const [editForm, setEditForm] = useState<UpdateCampaignRequest>({name: '', description: null, dueDate: '', version: 0})
    const [, setOwnerSearchQuery] = useState('')

    const {
        page, pageSize, sortBy, sortDir, queryString,
        setSort, setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: CertificationReviewControllerReviewItemsEndpoint,
        defaultSortBy: CertificationReviewControllerReviewItemsEndpoint.sort.STATUS,
        defaultSortDir: SortDir.ASC,
        sortable: true,
    })

    const {data: campaign, isLoading, error} = useQuery({
        queryKey: ['certification-campaign', id],
        queryFn: () => services.certificationCampaigns.detail(id!),
        enabled: !!id,
    })

    const {data: statistics} = useQuery({
        queryKey: ['certification-campaign', id, 'statistics'],
        queryFn: () => services.certification.campaignStatistics(id!),
        enabled: !!id && !!campaign && campaign.status !== CertificationCampaignStatus.STAGING,
    })

    const {data: previewStatistics} = useQuery({
        queryKey: ['certification-campaign', id, 'preview-statistics'],
        queryFn: () => services.certification.campaignPreviewStatistics(id!),
        enabled: !!id && !!campaign && campaign.status === CertificationCampaignStatus.STAGING,
    })

    // Secondary sort by identity display name provides deterministic ordering
    // within the same primary sort value. Both paths are declared in the Java
    // @SortableFields set so the runtime validator accepts the double sort=.
    const fullQueryString = useMemo(() => {
        const secondarySortKey = CertificationReviewControllerReviewItemsEndpoint.sort.IDENTITY_PARTY_DISPLAY_NAME
        return `${queryString}&${PageRequestFields.sort}=${secondarySortKey},${SortDir.ASC}`
    }, [queryString])

    const isStaging = campaign?.status === CertificationCampaignStatus.STAGING

    const {data: reviewItemsData} = useQuery({
        queryKey: ['certification-campaign-items', id, fullQueryString],
        queryFn: () => services.certification.listReviewItems(id!, Object.fromEntries(new URLSearchParams(fullQueryString))),
        enabled: !!id && !isStaging,
    })
    const reviewItems = reviewItemsData?.content || []
    const totalItems = reviewItemsData?.totalElements || 0
    const totalPages = reviewItemsData?.totalPages || 0
    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
    }, [searchAndFilters])

    const activateMutation = useEntityMutation({
        mutationFn: () => services.certification.activateCampaign(id!),
        invalidate: [
            ['certification-campaigns'],
            ['certification-dashboard-summary'],
            ['certification-dashboard-active-campaigns'],
        ],
        successMessage: i18n.certificationCampaigns.campaignActivated,
        onSuccess: () => {
            navigate(routes.accessGovernance.certificationDashboard.path())
        },
    })

    const cancelMutation = useEntityMutation({
        mutationFn: () => services.certification.cancelCampaign(id!),
        invalidate: [
            ['certification-campaign', id],
            ['certification-campaigns'],
        ],
        successMessage: i18n.certificationCampaigns.campaignCancelled,
        closeDialog: cancelDialog.close,
    })

    const deleteMutation = useEntityMutation({
        mutationFn: () => services.certificationCampaigns.remove(id!),
        invalidate: ['certification-campaigns'],
        successMessage: i18n.certificationCampaigns.campaignDeleted,
        onSuccess: () => {
            navigate(routes.accessGovernance.certificationDashboard.path())
        },
    })

    // WHY: 912.04 — owner edits move to the new POST/DELETE /{id}/owners endpoints;
    // the campaign edit dialog no longer carries a single-owner field. Multi-owner
    // management ships in the OwnersSection below.

    const updateMutation = useEntityMutation({
        mutationFn: (request: UpdateCampaignRequest) =>
            services.certification.updateCampaign(id!, request),
        invalidate: [
            ['certification-campaign', id],
            ['certification-campaigns'],
        ],
        successMessage: i18n.certificationCampaigns.campaignUpdated,
        closeDialog: editDialog.close,
    })

    const openEditDialog = () => {
        if (!campaign) return
        setEditForm({
            name: campaign.name,
            description: campaign.description,
            dueDate: campaign.dueDate ?? '',
            version: campaign.version,
        })
        setOwnerSearchQuery('')
        editDialog.open()
    }

    const statusLabel = (status: CertificationCampaignStatus) => {
        switch (status) {
            case CertificationCampaignStatus.STAGING:
                return i18n.certificationCampaigns.statusStaging
            case CertificationCampaignStatus.BEFORE_GENERATION:
                return i18n.certificationCampaigns.statusBeforeGeneration
            case CertificationCampaignStatus.IN_GENERATION:
                return i18n.certificationCampaigns.statusInGeneration
            case CertificationCampaignStatus.STARTED:
                return i18n.certificationCampaigns.statusStarted
            case CertificationCampaignStatus.COMPLETED:
                return i18n.certificationCampaigns.statusCompleted
            case CertificationCampaignStatus.CANCELLED:
                return i18n.certificationCampaigns.statusCancelled
            default:
                return status
        }
    }

    const reviewStatusLabel = (status: ReviewItemStatus) => {
        switch (status) {
            case ReviewItemStatus.PENDING:
                return i18n.certificationReviews.statusPending
            case ReviewItemStatus.IN_REVIEW:
                return i18n.certificationReviews.statusInReview
            case ReviewItemStatus.COMPLETED:
                return i18n.certificationReviews.statusCompleted
            default:
                return status
        }
    }

    return (
        <PageLayout
            data-testid="certification-campaign-detail-page"
            variant="detail"
            title={campaign?.name ?? i18n.certificationCampaigns.pageTitle}
            icon={CONCEPT_ICON.certificationCampaign}
            backTo={routes.accessGovernance.certificationCampaigns.list()}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(campaign, 'CAMPAIGN') : undefined)}
            description={campaign ? (
                <InlineRow>
                    {(() => {
                        const campaignBadge = CertificationCampaignStatusBadge.of(campaign.status)
                        const CampaignIcon = campaignBadge?.icon
                        return (
                            <Badge variant={campaignBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1" data-testid="campaign-status">
                                {CampaignIcon && <CampaignIcon className="h-3 w-3" aria-hidden="true"/>}
                                {statusLabel(campaign.status)}
                            </Badge>
                        )
                    })()}
                    {campaign.description && (
                        <MutedText data-testid="campaign-description">{campaign.description}</MutedText>
                    )}
                </InlineRow>
            ) : undefined}
            actions={canManage && campaign ? (
                <InlineRow>
                    {campaign.status === CertificationCampaignStatus.STAGING && (
                        <>
                            <Button variant="outline" onClick={openEditDialog} data-testid="btn-edit-campaign">
                                <Icon icon={Pencil} className="mr-2"/>
                                {i18n.certificationCampaigns.editCampaign}
                            </Button>
                            <Button variant="outline" onClick={deleteDialog.open} data-testid="btn-delete-campaign">
                                <Icon icon={Trash2} className="mr-2"/>
                                {i18n.certificationCampaigns.deleteCampaign}
                            </Button>
                            <Button onClick={activateDialog.open} data-testid="btn-activate-campaign">
                                <Icon icon={Play} className="mr-2"/>
                                {i18n.certificationCampaigns.activate}
                            </Button>
                        </>
                    )}
                    {(campaign.status === CertificationCampaignStatus.STAGING || campaign.status === CertificationCampaignStatus.BEFORE_GENERATION || campaign.status === CertificationCampaignStatus.STARTED) && (
                        <Button variant="destructive" onClick={cancelDialog.open} data-testid="btn-cancel-campaign">
                            <Icon icon={XCircle} className="mr-2"/>
                            {i18n.certificationCampaigns.cancel}
                        </Button>
                    )}
                </InlineRow>
            ) : undefined}
        >
            {campaign && <>
                <DetailsCard
                    data-testid="card-campaign-details"
                    title={i18n.common.details}
                    columns={3}
                    fields={[
                        {
                            label: i18n.certificationCampaigns.policy,
                            value: campaign.policy
                                ? <TextLink to={routes.accessGovernance.certificationPolicies.detail(campaign.policy.id)} testId="campaign-policy">{campaign.policy.displayName}</TextLink>
                                : undefined,
                        },
                        {
                            label: i18n.certificationCampaigns.origin,
                            value: (
                                <span data-testid="campaign-origin">
                                    {campaign.origin === CampaignOrigin.POLICY_GENERATED && campaign.policy
                                        ? <TextLink to={routes.accessGovernance.certificationPolicies.detail(campaign.policy.id)} testId="campaign-origin-policy-link">{i18n.certificationCampaigns.originPolicyGeneratedWithPolicy({policyName: campaign.policy.displayName})}</TextLink>
                                        : <span>{campaignOriginLabel(campaign.origin, i18n.certificationCampaigns)}</span>
                                    }
                                </span>
                            ),
                        },
                        {label: i18n.certificationCampaigns.dueDate, value: formatDate(campaign.dueDate)},
                        {label: i18n.certificationCampaigns.startedAt, value: formatTimestamp(campaign.startedAt)},
                        {label: i18n.certificationCampaigns.completedAt, value: formatTimestamp(campaign.completedAt)},
                        {label: i18n.common.createdAt, value: formatTimestamp(campaign.createdAt)},
                    ]}
                />

                {id && (
                    <OwnersSection
                        entityType={OwnersEntityType.certificationCampaigns}
                        entityId={id}
                        canEdit={canManage}
                        responsibilityOptions={certificationCampaignResponsibilityTypes}
                    />
                )}

                {campaign.status === CertificationCampaignStatus.STAGING && previewStatistics && (
                    <Card>
                        <CardHeader>
                            <CardTitle data-testid="card-title-campaign-preview">{i18n.certificationCampaigns.previewTitle}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <DetailGrid columns={2} className="mb-4">
                                <div>
                                    <MutedText size="sm">{i18n.certificationCampaigns.previewIdentityCount}</MutedText>
                                    <StatValue value={previewStatistics.identityCount}/>
                                </div>
                                <div>
                                    <MutedText size="sm">{i18n.certificationCampaigns.previewAssignmentCount}</MutedText>
                                    <StatValue value={previewStatistics.assignmentCount}/>
                                </div>
                            </DetailGrid>
                            <InfoBanner variant="muted" icon={Info}>
                                <MutedText>{i18n.certificationCampaigns.previewExplanation}</MutedText>
                            </InfoBanner>
                        </CardContent>
                    </Card>
                )}

                {campaign.status !== CertificationCampaignStatus.STAGING && statistics && (() => {
                    const stats = statistics
                    const reviewPercent = stats.totalReviewItems > 0
                        ? Math.round((stats.completedReviewItems / stats.totalReviewItems) * 100)
                        : 0
                    const totalDecisions = stats.keepCount + stats.revokedCount
                    const pendingCount = stats.totalAssignments - totalDecisions
                    const certifiedPercent = stats.totalAssignments > 0
                        ? Math.round((stats.keepCount / stats.totalAssignments) * 100)
                        : 0
                    const revokedPercent = stats.totalAssignments > 0
                        ? Math.round((stats.revokedCount / stats.totalAssignments) * 100)
                        : 0

                    return (
                        <Card>
                            <CardHeader>
                                <CardTitle data-testid="card-title-campaign-statistics">{i18n.certificationCampaigns.statistics}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Stack gap="lg">
                                    {/* Review Items Progress */}
                                    <Stack gap="sm" data-testid="stat-review-progress">
                                        <InlineRow justify="between" align="baseline" className="text-sm">
                                            <Strong>{i18n.certificationCampaigns.reviewItemsProgress}</Strong>
                                            <MutedText className="tabular-nums whitespace-nowrap" data-testid="stat-review-progress-count">
                                                {stats.completedReviewItems}/{stats.totalReviewItems}
                                            </MutedText>
                                        </InlineRow>
                                        <Progress value={reviewPercent} className="h-3"/>
                                    </Stack>

                                    {/* Decisions Breakdown */}
                                    <Stack gap="sm" data-testid="stat-decisions">
                                        <InlineRow justify="between" align="baseline" className="text-sm">
                                            <Strong>{i18n.certificationCampaigns.decisionsBreakdown}</Strong>
                                            <MutedText className="tabular-nums whitespace-nowrap">
                                                {totalDecisions}/{stats.totalAssignments}
                                            </MutedText>
                                        </InlineRow>
                                        {/* Stacked bar */}
                                        <SegmentedProgress
                                            segments={totalDecisions > 0 ? [
                                                {percent: certifiedPercent, tone: 'success'},
                                                {percent: revokedPercent, tone: 'danger'},
                                            ] : []}
                                        />
                                        {/* Legend */}
                                        <InlineRow gap="lg">
                                            <InlineRow gap="sm">
                                                <PulseDot tone="success" size="md" staticDot/>
                                                <MutedText size="xs">{i18n.certificationCampaigns.keepCount} ({stats.keepCount})</MutedText>
                                            </InlineRow>
                                            <InlineRow gap="sm">
                                                <PulseDot tone="danger" size="md" staticDot/>
                                                <MutedText size="xs">{i18n.certificationCampaigns.revokedCount} ({stats.revokedCount})</MutedText>
                                            </InlineRow>
                                            <InlineRow gap="sm">
                                                <PulseDot tone="muted" size="md" staticDot/>
                                                <MutedText size="xs">{i18n.certificationCampaigns.pendingDecisions} ({pendingCount})</MutedText>
                                            </InlineRow>
                                        </InlineRow>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    )
                })()}

                {campaign.status !== CertificationCampaignStatus.STAGING && <Card>
                    <CardHeader>
                        <SplitRow>
                            <div>
                                <CardTitle data-testid="card-title-review-items">{i18n.certificationCampaigns.reviewItems}</CardTitle>
                                <CardDescription data-testid="review-items-description">
                                    {`${totalItems} ${i18n.certificationCampaigns.reviewItems.toLowerCase()}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
                                </CardDescription>
                                <span data-testid="campaign-review-items-count">{totalItems}</span>
                            </div>
                            <UnifiedSearchBar
                                contract={CertificationReviewControllerReviewItemsEndpoint}
                                value={searchAndFilters}
                                onChange={setSearchAndFilters}
                                placeholder={i18n.certificationCampaigns.searchReviewItems as string}
                                testIdNamespace="campaign-review-items"
                            />
                        </SplitRow>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-identity">{i18n.certificationReviews.identity}</TableHead>
                                    <TableHead data-testid="col-reviewer">{i18n.certificationReviews.reviewer}</TableHead>
                                    <TableHead data-testid="col-status">
                                        <SortableHeader
                                            label={i18n.certificationReviews.status}
                                            sortKey={CertificationReviewItemResponseFields.status}
                                            currentSortBy={sortBy}
                                            currentSortDir={sortDir}
                                            onSort={setSort}
                                        />
                                    </TableHead>
                                    <TableHead data-testid="col-total-assignments">
                                        <SortableHeader
                                            label={i18n.certificationReviews.totalAssignments}
                                            sortKey={CertificationReviewItemResponseFields.totalAssignments}
                                            currentSortBy={sortBy}
                                            currentSortDir={sortDir}
                                            onSort={setSort}
                                        />
                                    </TableHead>
                                    <TableHead data-testid="col-decided-assignments">
                                        <SortableHeader
                                            label={i18n.certificationReviews.decidedAssignments}
                                            sortKey={CertificationReviewItemResponseFields.decidedAssignments}
                                            currentSortBy={sortBy}
                                            currentSortDir={sortDir}
                                            onSort={setSort}
                                        />
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {reviewItems.map((item) => {
                                    // eslint-disable-next-line i18next/no-literal-string
                                    const reviewUrl = `${routes.accessGovernance.certificationReviews.assignments(item.id)}?readonly=true`
                                    // eslint-disable-next-line i18next/no-literal-string -- internal routing state
                                    const reviewNavState = {from: 'campaign' as const, campaignId: id}
                                    return (<ClickableTableRow key={item.id} data-testid={`review-item-row-${item.id}`} to={reviewUrl} state={reviewNavState}>
                                        <TableCell className="font-medium" data-testid={`review-item-identity-${item.id}`}>
                                            <TextLink to={reviewUrl} state={reviewNavState} testId={`link-review-item-${item.id}`} stopPropagation>
                                                {item.identity.displayName}
                                            </TextLink>
                                        </TableCell>
                                        <TableCell>
                                            <InlineRow gap="sm">
                                                {item.reviewer?.displayName ?? '—'}
                                                {item.reviewer && campaign.owners[0] && item.reviewer.id === campaign.owners[0].ownerPartyId && (
                                                    <Badge variant="outline" className="text-xs" data-testid="fallback-reviewer-badge">{i18n.certificationReviews.fallbackReviewer}</Badge>
                                                )}
                                            </InlineRow>
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const reviewBadge = ReviewItemStatusBadge.of(item.status)
                                                const ReviewIcon = reviewBadge?.icon
                                                return (
                                                    <Badge variant={reviewBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1" data-testid={`review-item-status-${item.id}`}>
                                                        {ReviewIcon && <ReviewIcon className="h-3 w-3" aria-hidden="true"/>}
                                                        {reviewStatusLabel(item.status)}
                                                    </Badge>
                                                )
                                            })()}
                                        </TableCell>
                                        <TableCell>{item.totalAssignments}</TableCell>
                                        <TableCell>{item.decidedAssignments}</TableCell>
                                    </ClickableTableRow>)
                                })}
                                {reviewItems.length === 0 && (
                                    <TableEmptyState
                                        colSpan={5}
                                        hasFilters={hasActiveFilters}
                                        matchMessage={i18n.certificationCampaigns.noReviewItemsMatch}
                                        emptyMessage={i18n.certificationCampaigns.noReviewItemsFound}
                                    />
                                )}
                            </TableBody>
                        </Table>

                        {totalItems > 0 && (
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalItems={totalItems}
                                totalPages={totalPages}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                pageSizeOptions={pageSizeOptions}
                            />
                        )}
                    </CardContent>
                </Card>}

            <Dialog open={activateDialog.isOpen} onOpenChange={activateDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationCampaigns.activate}</DialogTitle>
                        <DialogDescription>{i18n.certificationCampaigns.activateConfirm}</DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="activate-dialog"
                        onCancel={activateDialog.close}
                        saving={activateMutation.isPending}
                        saveLabel={i18n.certificationCampaigns.activate}
                        saveType="button"
                        onSave={() => activateMutation.mutate()}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={cancelDialog.isOpen} onOpenChange={cancelDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationCampaigns.cancel}</DialogTitle>
                        <DialogDescription>{i18n.certificationCampaigns.cancelConfirm}</DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="cancel-dialog"
                        onCancel={cancelDialog.close}
                        saving={cancelMutation.isPending}
                        saveLabel={i18n.certificationCampaigns.cancel}
                        destructive
                        saveType="button"
                        onSave={() => cancelMutation.mutate()}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={deleteDialog.isOpen} onOpenChange={deleteDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationCampaigns.deleteCampaign}</DialogTitle>
                        <DialogDescription>
                            {i18n.certificationCampaigns.deleteConfirm({name: campaign?.name ?? ''})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete"
                        onCancel={deleteDialog.close}
                        saving={deleteMutation.isPending}
                        saveLabel={i18n.common.delete}
                        savingLabel={i18n.common.deleting}
                        destructive
                        saveType="button"
                        onSave={() => deleteMutation.mutate()}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationCampaigns.editCampaign}</DialogTitle>
                        <DialogDescription>{i18n.certificationCampaigns.editDescription}</DialogDescription>
                    </DialogHeader>
                    <FormGrid className="py-4">
                        <FormField label={i18n.certificationCampaigns.name} htmlFor="edit-campaign-name">
                            <Input
                                id="edit-campaign-name"
                                value={editForm.name}
                                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                            />
                        </FormField>
                        <FormField label={i18n.certificationCampaigns.description} htmlFor="edit-campaign-description">
                            <Input
                                id="edit-campaign-description"
                                value={editForm.description ?? ''}
                                onChange={(e) => setEditForm({...editForm, description: e.target.value || null})}
                            />
                        </FormField>
                        <FormField label={i18n.certificationCampaigns.dueDate} htmlFor="edit-campaign-due-date">
                            <DatePicker
                                testId="edit-campaign-due-date"
                                value={editForm.dueDate}
                                onChange={(iso) => setEditForm({...editForm, dueDate: iso})}
                                ariaLabel={String(i18n.certificationCampaigns.dueDate)}
                            />
                        </FormField>
                    </FormGrid>
                    <SaveCancelFooter
                        testIdPrefix="edit"
                        onCancel={editDialog.close}
                        saving={updateMutation.isPending}
                        disabled={!editForm.name || !editForm.dueDate}
                        saveType="button"
                        onSave={() => updateMutation.mutate(editForm)}
                    />
                </DialogContent>
            </Dialog>
        </>}
        </PageLayout>
    )
}
